// Right-rail inspector for a single cabinet part. Mounted by
// ProjectEditorPage when the outliner's secondary selection points
// to a part inside a cabinet (id may be `left_side`, `drawer_3_face`,
// `shelf_0`, …).
//
// Shows the read-only summary the user just picked (dims, role,
// thickness, material, banding, drilling count) and an "Edit details"
// button that opens the existing PartEditor modal for deep edits
// (extra drillings, per-edge band selection, suppression, etc.).

import { useMemo } from "react";
import type { Cabinet, CabinetPart, EdgeBand, Id, PartRole } from "@/lib/types";
import { partsFor, autoDrillingsFor, type CatalogIndex } from "@/lib/cabinets/bom";
import { getCabinetClass } from "@/lib/cabinets";
import { PartPreview } from "./PartPreview";

interface Props {
  cabinet: Cabinet;
  partId: string;
  catalog: CatalogIndex;
  edgeBands: EdgeBand[];
  /** Patch partOverrides[partId].enabled / .thickness / etc. */
  onPartOverride: (partId: string, patch: Partial<NonNullable<Cabinet["partOverrides"]>[string]>) => void;
  /** Open the deep PartEditor modal pre-selected on this part. */
  onOpenPartEditor: () => void;
  /** Back to the parent cabinet's inspector. */
  onSelectCabinet: (id: Id) => void;
}

export function PartInspector({
  cabinet, partId, catalog, edgeBands,
  onPartOverride, onOpenPartEditor, onSelectCabinet,
}: Props) {
  const cls = getCabinetClass(cabinet.classId);

  // Resolve the part by id from the full evaluated parts list (so we
  // pick up drawer parts and post-override material).
  const parts = useMemo(() => partsFor(cabinet, catalog), [cabinet, catalog]);
  const part: CabinetPart | undefined = parts.find((p) => p.id === partId);

  // For the preview we want the FULL standard pattern (with suppressed
  // holes rendered faded), not the post-suppress effectiveDrillings()
  // result. Pull the raw pattern off the class for this part id.
  const stdDrillings = useMemo(() => {
    if (!part) return [];
    const cls = getCabinetClass(cabinet.classId);
    if (!cls) return [];
    const pattern = cls.drillPattern({ w: cabinet.width, h: cabinet.height, d: cabinet.depth });
    return pattern[part.id] ?? [];
  }, [cabinet, part]);

  // Per-cabinet auto-drillings the class can't see — drawer slide-rail
  // screws on the side panels + leg-mount screws on the bottom panel.
  const autoDrillings = useMemo(
    () => (part ? autoDrillingsFor(cabinet, part.id) : []),
    [cabinet, part],
  );

  const override = cabinet.partOverrides?.[partId];

  if (!part) {
    return (
      <aside style={asideStyle}>
        <button className="ghost" style={backBtn} onClick={() => onSelectCabinet(cabinet._id)}>
          ← {cls?.label ?? "Cabinet"} #{cabinet._id}
        </button>
        <div className="muted" style={{ fontSize: 12, marginTop: 18 }}>
          Part <code>{partId}</code> is not in this cabinet's current evaluation
          (maybe disabled or removed via overrides).
        </div>
      </aside>
    );
  }

  const material = catalog.materials.get(part.materialId);
  const suppressedSet   = new Set(override?.suppressDrillings ?? []);
  const suppressedCount = suppressedSet.size;
  const extrasCount     = (override?.extraDrillings?.length ?? 0);
  const autoCount       = autoDrillings.length;
  const totalStdCount   = stdDrillings.length;
  const activeStdCount  = totalStdCount - suppressedCount;

  return (
    <aside style={asideStyle}>
      <button className="ghost" style={backBtn} onClick={() => onSelectCabinet(cabinet._id)}>
        ← {cls?.label ?? "Cabinet"} #{cabinet._id}
      </button>

      <div className="label">PART</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
        {part.name}
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        <code>{part.id}</code>{part.role ? ` · ${roleLabel(part.role)}` : ""}
      </div>

      <div style={{ marginTop: 12 }}>
        <PartPreview
          part={part}
          stdDrillings={stdDrillings}
          autoDrillings={autoDrillings}
          extras={override?.extraDrillings ?? []}
          suppressedSet={suppressedSet}
          edgeBands={edgeBands}
          onToggleSuppress={() => { /* deep edits in modal */ }}
          height={170}
        />
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, alignItems: "center", fontSize: 12 }}>
        <span className="muted">Dimensions</span>
        <span>{part.w.toFixed(2)}″ × {part.h.toFixed(2)}″ × {part.thickness.toFixed(3)}″</span>

        <span className="muted">Material</span>
        <span>{material ? material.name : <span style={{ color: "var(--fg-dim)" }}>(unset)</span>}</span>

        <span className="muted">Grain</span>
        <span>{part.grain}</span>

        <span className="muted">Quantity</span>
        <span>{part.qty ?? 1}</span>

        <span className="muted">Standard drillings</span>
        <span>
          {activeStdCount} of {totalStdCount}
          {suppressedCount > 0 && <span className="muted"> ({suppressedCount} suppressed)</span>}
        </span>

        <span className="muted">Custom drillings</span>
        <span>{extrasCount}</span>

        {autoCount > 0 && <>
          <span className="muted">Auto drillings</span>
          <span title="Drawer-slide + leg-mount screws — automatically placed">
            {autoCount} <span className="muted" style={{ fontSize: 10 }}>(slides/legs)</span>
          </span>
        </>}

        <span className="muted">Enabled</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={override?.enabled !== false}
            onChange={(e) => onPartOverride(part.id, { enabled: e.target.checked ? undefined : false })}
          />
          <span className="muted" style={{ fontSize: 11 }}>
            Uncheck to remove this part from the cabinet
          </span>
        </label>
      </div>

      <button
        onClick={onOpenPartEditor}
        style={{ marginTop: 16, width: "100%" }}
      >
        Edit details (edges, drillings) →
      </button>
    </aside>
  );
}

function roleLabel(r: PartRole): string {
  return String(r).replace(/_/g, " ");
}

const asideStyle: React.CSSProperties = {
  borderLeft: "1px solid var(--line)",
  padding: 16,
  overflowY: "auto",
};
const backBtn: React.CSSProperties = {
  fontSize: 11, padding: "2px 8px", marginBottom: 8,
};
